/**
 * Dataset de MUESTRA de municipios — modo "Red nacional CALE".
 *
 * [DISEÑO] Este NO es el padrón completo: la cifra oficial DANE/DIVIPOLA es
 * 1.122 municipios [EVIDENCIA] (el brief menciona 1.103 → brecha de 19
 * declarada [POR VERIFICAR], ver docs/DICCIONARIO_DATOS.md). La base real
 * no ha sido entregada; cuando llegue, este archivo se reemplaza por un
 * generador sobre la base y se registra hashArchivo en DataMeta.
 *
 * Reglas del dataset de muestra:
 * - Todos los departamentos representados (61 municipios reales).
 * - poblacion: cifra aproximada de proyección DANE [EVIDENCIA aproximada].
 * - demandaObservada / proyeccion2030 / tiempoViajeMin: null = [POR VERIFICAR].
 *   NUNCA inventar demanda RUNT ni tiempos de viaje reales.
 */
export const FUENTE_MUNICIPIOS =
  "DANE — DIVIPOLA (enero 2024) + proyecciones de población DANE (aprox.)";
export const FECHA_CORTE_MUNICIPIOS = "2026-09-27";

export interface Municipio {
  /** Código DIVIPOLA del municipio (5 dígitos: 2 depto + 3 mpio). */
  codigoDivipola: string;
  nombre: string;
  /** Código DIVIPOLA del departamento (2 dígitos). */
  codigoDepartamento: string;
  departamento: string;
  lat: number;
  lon: number;
  /** Proyección DANE aproximada [EVIDENCIA aproximada, cifra redondeada]. */
  poblacion: number;
  /** Aspirantes/año observados. null = [POR VERIFICAR] (sin base RUNT). */
  demandaObservada: null;
  /** Proyección de demanda 2030. null = [POR VERIFICAR]. */
  proyeccion2030: null;
  /** Tiempo de viaje al nodo/sede más cercano (min). null = [POR VERIFICAR]. */
  tiempoViajeMin: null;
  /** Estado probatorio del registro completo. */
  estadoProbatorio: "sin-dato";
}

type MunicipioSeed = [
  codigo: string,
  nombre: string,
  lat: number,
  lon: number,
  poblacion: number,
];

function mpio(
  seed: MunicipioSeed,
  dptoCodigo: string,
  dptoNombre: string,
): Municipio {
  return {
    codigoDivipola: seed[0],
    nombre: seed[1],
    codigoDepartamento: dptoCodigo,
    departamento: dptoNombre,
    lat: seed[2],
    lon: seed[3],
    poblacion: seed[4],
    demandaObservada: null,
    proyeccion2030: null,
    tiempoViajeMin: null,
    estadoProbatorio: "sin-dato",
  };
}

const D: Record<string, string> = {
  "05": "Antioquia",
  "08": "Atlántico",
  "11": "Bogotá, D.C.",
  "13": "Bolívar",
  "15": "Boyacá",
  "17": "Caldas",
  "18": "Caquetá",
  "19": "Cauca",
  "20": "Cesar",
  "23": "Córdoba",
  "25": "Cundinamarca",
  "27": "Chocó",
  "41": "Huila",
  "44": "La Guajira",
  "47": "Magdalena",
  "50": "Meta",
  "52": "Nariño",
  "54": "Norte de Santander",
  "63": "Quindío",
  "66": "Risaralda",
  "68": "Santander",
  "70": "Sucre",
  "73": "Tolima",
  "76": "Valle del Cauca",
  "81": "Arauca",
  "85": "Casanare",
  "86": "Putumayo",
  "88": "Archipiélago de San Andrés, Providencia y Santa Catalina",
  "91": "Amazonas",
  "94": "Guainía",
  "95": "Guaviare",
  "97": "Vaupés",
  "99": "Vichada",
};

const SEEDS: ReadonlyArray<readonly [string, MunicipioSeed[]]> = [
  ["05", [["05001", "Medellín", 6.244, -75.581, 2500000], ["05266", "Envigado", 6.17, -75.583, 240000], ["05088", "Bello", 6.337, -75.562, 560000], ["05615", "Rionegro", 6.155, -75.374, 140000], ["05045", "Apartadó", 7.883, -76.633, 200000]]],
  ["08", [["08001", "Barranquilla", 10.969, -74.8, 1300000], ["08758", "Soledad", 10.918, -74.767, 680000]]],
  ["11", [["11001", "Bogotá, D.C.", 4.711, -74.072, 7900000]]],
  ["13", [["13001", "Cartagena", 10.4, -75.5, 1060000], ["13430", "Magangué", 9.242, -74.755, 120000]]],
  ["15", [["15001", "Tunja", 5.545, -73.367, 205000], ["15238", "Duitama", 5.824, -73.033, 130000]]],
  ["17", [["17001", "Manizales", 5.07, -75.52, 460000], ["17380", "La Dorada", 5.453, -74.663, 80000]]],
  ["18", [["18001", "Florencia", 1.614, -75.606, 190000]]],
  ["19", [["19001", "Popayán", 2.444, -76.614, 340000], ["19698", "Santander de Quilichao", 3.0, -76.483, 110000]]],
  ["20", [["20001", "Valledupar", 10.474, -73.243, 540000]]],
  ["23", [["23001", "Montería", 8.748, -75.881, 520000], ["23417", "Lorica", 9.237, -75.815, 125000]]],
  ["25", [["25754", "Soacha", 4.579, -74.217, 720000], ["25269", "Facatativá", 4.813, -74.354, 160000], ["25899", "Zipaquirá", 5.022, -74.005, 140000], ["25307", "Girardot", 4.304, -74.8, 110000]]],
  ["27", [["27001", "Quibdó", 5.692, -76.658, 130000]]],
  ["41", [["41001", "Neiva", 2.927, -75.288, 380000], ["41551", "Pitalito", 1.899, -76.042, 150000]]],
  ["44", [["44001", "Riohacha", 11.544, -72.906, 310000], ["44430", "Maicao", 11.383, -72.24, 170000]]],
  ["47", [["47001", "Santa Marta", 11.24, -74.2, 610000], ["47189", "Ciénaga", 11.007, -74.247, 110000]]],
  ["50", [["50001", "Villavicencio", 4.142, -73.627, 550000], ["50006", "Acacías", 3.988, -73.758, 80000]]],
  ["52", [["52001", "Pasto", 1.208, -77.277, 400000], ["52356", "Ipiales", 0.825, -77.64, 140000], ["52835", "Tumaco", 1.806, -78.765, 260000]]],
  ["54", [["54001", "Cúcuta", 7.894, -72.504, 800000], ["54498", "Ocaña", 8.233, -73.35, 120000]]],
  ["63", [["63001", "Armenia", 4.534, -75.681, 310000]]],
  ["66", [["66001", "Pereira", 4.814, -75.694, 490000], ["66170", "Dosquebradas", 4.836, -75.672, 210000]]],
  ["68", [["68001", "Bucaramanga", 7.119, -73.12, 610000], ["68276", "Floridablanca", 7.064, -73.097, 320000], ["68081", "Barrancabermeja", 7.065, -73.855, 230000]]],
  ["70", [["70001", "Sincelejo", 9.295, -75.396, 300000]]],
  ["73", [["73001", "Ibagué", 4.438, -75.2, 570000], ["73268", "Espinal", 4.149, -74.882, 80000]]],
  ["76", [["76001", "Cali", 3.437, -76.522, 2280000], ["76520", "Palmira", 3.539, -76.303, 360000], ["76109", "Buenaventura", 3.88, -77.031, 350000], ["76834", "Tuluá", 4.083, -76.2, 220000]]],
  ["81", [["81001", "Arauca", 7.084, -70.759, 110000]]],
  ["85", [["85001", "Yopal", 5.349, -72.396, 170000], ["85010", "Aguazul", 5.172, -72.547, 40000]]],
  ["86", [["86001", "Mocoa", 1.149, -76.647, 60000]]],
  ["88", [["88001", "San Andrés", 12.583, -81.7, 60000]]],
  ["91", [["91001", "Leticia", -4.215, -69.94, 55000]]],
  ["94", [["94001", "Inírida", 3.865, -67.924, 35000]]],
  ["95", [["95001", "San José del Guaviare", 2.571, -72.641, 80000]]],
  ["97", [["97001", "Mitú", 1.253, -70.234, 40000]]],
  ["99", [["99001", "Puerto Carreño", 6.189, -67.486, 25000]]],
];

export const MUNICIPIOS_SAMPLE: readonly Municipio[] = SEEDS.flatMap(
  ([dptoCodigo, seeds]) => seeds.map((s) => mpio(s, dptoCodigo, D[dptoCodigo])),
);
